import {
  B2bWithdrawalOrdersFlow,
  B2bWithdrawalStatus,
  B2bWithdrawalType,
  GetB2bWithdrawalsResponse,
} from '@Common/types/bo/b2b/withdrawals';
import { B2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { MdlrPaymentStatus } from '@Common/types/modulr';

const withdrawalFields: Record<
  | keyof GetB2bWithdrawalsResponse[number]
  | 'orders'
  | 'b2b_company_plan_ids'
  | 'b2b_campaign_id'
  | 'savings_funds_requests_id'
  | 'b2b_flags'
  | 'assets_class',
  string
> = {
  id: 'ID Retrait',
  user_id: 'ID Utilisateur',
  company_id: 'ID Entreprise',
  b2b_company_plan_ids: "ID Plans d'épargne",
  b2b_campaign_id: 'ID Campagne',
  investing_product: "Produit d'épargne",
  assets_class: "Classe d'actifs",
  mdlr_beneficiary_id: 'ID bénéficiaire Modulr',
  savings_funds_requests_id: 'ID Appel de fonds',
  account_type: 'Type de compte',
  sort_code: 'Code de tri bancaire',
  account_number: 'Numéro de compte',
  bank_country: 'Pays de domiciliation bancaire',
  contract_id: 'ID Contrat',
  status: 'Statut',
  type: 'Type',
  orders_flow: 'Flux des ordres',
  is_planned_withdrawal: 'Retrait planifié',
  gross_amount: 'Montant brut',
  net_amount: 'Montant net',
  tax_amount: 'Montant taxe',
  initial_amount: 'Montant initial',
  orders: 'Ordres',
  currency: 'Devise',
  task_state: 'Statut Tâche',
  task_assignee_group: 'Groupe assigné',
  task_assignee: 'Personne assignée',
  bo_task_id: 'ID Tâche',
  early_withdraw_reason: 'Motif de retrait anticipé',
  early_withdraw_event_date: "Date de l'évenement générateur",
  employee_id: 'ID Adhérent ',
  created_at: 'Créé le',
  paid_at: 'Payé à',
  payment_status: 'Statut du paiement',
  updated_at: 'Mis à jour le',
  correlation_id: 'ID de corrélation',
  b2b_flags: "B2b flags de l'utilisateur",
  firstname: 'Prénom',
  lastname: 'Nom',
};

const bankAccountTypes = {
  iban: 'IBAN',
  intl: 'International',
  scan: 'SCAN (UK)',
  dd: 'Prélèvement automatique',
};

export const b2bWithdrawalsCreateTranslations = {
  title: 'Créer une demande de retrait',
  steps: {
    1: 'Informations générales',
    2: 'Montants',
    3: 'Paiement',
    4: 'Documents',
  },
  sections: {
    affiliate_information: "Informations sur l'affilié",
    from: 'Demande de retrait depuis',
    payment: 'Paiement',
    documents_identity: "Documents d'identité de l'adhérent",
    documents_supporting: 'Documents justificatifs',
  },
  suspended: {
    title: 'Fonds sur des instruments suspendus : %{total}',
    description:
      'Ces fonds appartiennent à des instruments suspendus et ne peuvent pas être retirés tant que les instruments ne sont pas réactivés.',
    not_withdrawable:
      'Tous les fonds disponibles appartiennent à des instruments suspendus et ne peuvent pas être retirés',
  },
  subtitles: {
    from: 'Sélectionnez le plan depuis lequel vous souhaitez retirer, ou commencez une demande de retrait anticipé',
    available: '%{available} € disponible',
    available_out: "%{available} € disponible aujourd'hui sur %{total} €",
    available_locked: '%{available} € disponible après déblocage',
  },
  errors: {
    no_employees_found: "Aucun employé trouvé pour l'entreprise",
    failure: 'Impossible de créer la demande de retrait : %{error}',
    pending_orders: 'Impossible de créer une demande de retrait avec des ordres en attente',
    deceased_requires_third_party:
      'Un retrait pour un épargnant décédé doit être versé sur un compte bancaire tiers',
    insufficient_balance: 'Solde insuffisant',
    no_beneficiaries: 'Aucun compte bancaire trouvé pour cet employé ou cette entreprise',
    duplicate_employee_name:
      'Un autre employé porte le même prénom et le même nom. Veuillez vous assurer que vous avez sélectionné le bon employé.',
    duplicate_employee_row: 'ID employé %{id} - %{company} - NIR %{nir}',
  },
  labels: {
    b2b_employee_id_search: "ID de l'employé / Nom / Prénom / NIR",
    available_funds: 'Fonds disponibles',
    early_request: 'Demande de retrait anticipé',
    amount: 'Montant à retirer',
    document_identity_still_valid: "Assurez-vous que le document d'identité est toujours valide",
    date_of_expiry: "Date d'expiration",
    date_of_event: "Date de l'événement",
    reason: 'Raison de la demande',
    no_funds: 'Aucun produit avec des fonds trouvé',
    amount_to_withdraw:
      'Montant net à retirer après contributions sociales sur la plus-value : EUR %{amount}',
    amounts_estimations_notice:
      "Les montants sont estimés sur la base des dernières valeurs liquidatives (VL) connues. Le montant final peut changer en raison d'ordres de vente à un prix inconnu.",
    select_bank_account: 'Sélectionnez un compte bancaire',
    employee_account_option: 'Compte employé - %{iban} - %{name}',
    company_account_option: 'Compte entreprise - %{iban} - %{name}',
    third_party_account_option: 'Compte bancaire tiers',
    third_party_details: 'Entrez les détails du compte bancaire tiers',
    third_party_only_deceased:
      "L'épargnant est décédé ou le motif est le décès de l'épargnant. Le retrait doit être versé sur un compte bancaire tiers.",
    iban: 'IBAN',
    bic: 'BIC',
    rib_upload: 'Document RIB (PDF)',
    account_type: 'Type de compte',
    account_type_iban: 'Compte IBAN',
    account_type_intl: 'Compte international',
    account_type_scan: 'Compte SCAN (UK)',
    sort_code: 'Code de tri bancaire',
    account_number: 'Numéro de compte',
    entity_type: 'Type de bénéficiaire',
    entity_individual: 'Particulier',
    entity_institution: 'Institution',
    first_name: 'Prénom',
    last_name: 'Nom',
    company_name: "Nom de l'entreprise",
    beneficiary_address: 'Adresse du bénéficiaire',
    address_street: 'Rue',
    address_city: 'Ville',
    address_postcode: 'Code postal',
    address_country: 'Pays',
  },
  validation: {
    date_of_expiry: "La date d'expiration ne doit pas être dans le passé",
    non_zero_allocation_required: 'Le montant total de retrait doit être supérieur à 0',
    beneficiary_required: 'Veuillez sélectionner un compte bancaire',
    third_party_required: 'Veuillez remplir tous les champs du compte bancaire tiers',
    scan_sort_code_invalid: 'Le code de tri bancaire doit contenir exactement 6 chiffres',
    scan_account_number_invalid: 'Le numéro de compte doit contenir exactement 8 chiffres',
    no_eligible_allocations:
      'Aucune allocation éligible trouvée pour la raison et la date sélectionnées',
  },
};

export const b2bWithdrawalsTranslations = {
  fields: withdrawalFields,
  bank_account_types: bankAccountTypes,
  title: {
    show: 'Retrait',
  },
  cancel: {
    label: 'Annuler',
    success: 'Retrait annulé avec succès',
    failure: "Échec de l'annulation du retrait : %{error}",
    confirm_title: 'Annuler ce retrait ?',
    confirm_content:
      'Le retrait sera annulé ainsi que son appel de fonds n°%{savings_funds_request_id} et ses ordres de vente CCB. La position CCB du salarié reste inchangée.',
    reason_label: 'Motif',
    description_label: 'Description (facultative)',
    confirm: "Confirmer l'annulation",
    disabled_reasons: {
      unsupported_assets_class: 'Seuls les retraits CCB peuvent être annulés ici',
      not_approved: 'Seuls les retraits approuvés peuvent être annulés',
      already_cancelled: 'Ce retrait est déjà annulé',
      payout_payment_exists: 'Un paiement a déjà été envoyé pour ce retrait',
    },
  },
  orders: {
    link: 'Lien',
  },
  sections: {
    details: 'Détails',
    early: 'Retrait anticipé',
    documents_requests: 'Documents de demande de retrait',
    ccb_instructions:
      "L'approbation de ce retrait entraînera une demande de fonds auprès de l'entreprise. Cette demande devra être traitée pour que le retrait puisse être effectué.",
    mdlr_beneficiary_id_missing:
      "Ce retrait n'a pas de bénéficiaire lié. Le paiement sera envoyé vers le bénéficiaire actif et approuvé de l'utilisateur.",
    destination_account_warning_high_risk_country:
      'Le compte destinataire est situé dans un pays à haut risque',
    destination_account_warning_country_under_surveillance:
      'Le compte destinataire est situé dans un pays sous surveillance',
    documents_identity: "Documents d'identité",
    b2b_proof_identity: "Tâches de preuve d'identité B2B",
    documents_identity_upload: "Déposer un document d'identité",
    same_withdrawal_request: 'De la même demande de retrait',
    linked_transfers: 'Transferts liés',
  },
  create: b2bWithdrawalsCreateTranslations,
  bulk_create: {
    steps: {
      1: 'Informations générales',
      2: 'Revue',
      3: 'Confirmation',
    },
    general_info: {
      header_1: "1. Ajouter le code ISIN du fonds à l'échéance",
      header_2: '2. Télécharger le modèle',
      header_3: '3. Compléter votre fichier',
      header_4: '4. Télécharger votre fichier complété',
      complete_file_description:
        'Ajoutez vos données au fichier en suivant le guide. Exécutez les contrôles pour vous assurer que tout est correct.',
    },
    confirmation: {
      title: 'Résumé et confirmation',
      operation_type: "Type d'opération : Retrait",
      destination_account: 'Compte de destination',
      total_gross_amount_withdrawal: 'Montant brut total du retrait : %{amount} EUR',
      number_of_employees_concerned: "Nombre d'employés concernés : %{count}",
      employee_accounts: 'Les comptes bancaires des employés',
      company_account: 'Compte entreprise - %{iban} - %{name}',
      company_account_missing: 'Compte entreprise',
      status: {
        pending: 'En attente',
        missing: 'Manquant',
      },
    },
    beneficiary_missing_modal: {
      title: 'Bénéficiaire entreprise manquant',
      body: "Cette entreprise n'a aucun bénéficiaire configuré pour le compte de paiement entreprise. Ajoutez un bénéficiaire avant de créer le retrait groupé.",
    },
    labels: {
      download_eligible_employees: 'Télécharger la liste des employés éligibles',
      upload_filled_template: 'Déposer le fichier template rempli (.xlsm)',
      no_rows_found: 'Aucun employé éligible trouvé',
      total_shares: 'Nombre total de parts',
      current_position: 'Position actuelle',
      company: 'Entreprise internationale',
      instrument: "Instrument ISIN (à l'échéance)",
      review_step_description:
        'Vérifiez les positions et les quantités avant de procéder au retrait. Toutes les pages du tableau doivent être consultées.',
    },
    validation: {
      upload_file_required: 'Veuillez déposer un template rempli avant de continuer',
      no_rows: 'Aucune ligne employé disponible pour soumission',
      review_all_pages_and_load_data:
        'Veuillez revoir toutes les pages et attendre le chargement de toutes les positions des employés avant de continuer',
      deceased_employees:
        'Certains employés sont décédés. Retirez-les du fichier ou mettez leur quantité à 0 avant de continuer, et créez leur retrait individuellement avec un compte bancaire tiers.',
    },
    errors: {
      select_company_and_instrument:
        "Veuillez d'abord sélectionner une entreprise et un instrument",
      template_generation_failed: 'Impossible de générer le template en masse',
      parse_file_failed: 'Impossible de lire le template déposé',
      load_positions_failed: 'Impossible de charger les positions actuelles pour tous les employés',
      create_failed: 'Impossible de créer les demandes de retrait en masse',
      deceased_employees:
        'Les employés suivants sont décédés et ne peuvent pas être inclus dans un retrait en masse. Retirez-les du fichier ou mettez leur quantité à 0, et créez leur retrait individuellement avec un compte bancaire tiers :',
    },
    success: {
      created: 'Demandes de retrait en masse créées',
      template_generated: 'Template généré',
    },
    success_modal: {
      title: 'Demande de retrait créée avec succès',
      description:
        'Votre transaction a été créée avec succès. Les retraits sont en cours de création. Vous pouvez les suivre via votre tableau de bord des retraits.',
      cta: 'Compris',
    },
    title: 'Créer une demande de retrait',
    button: 'Création en masse',
  },
  inputs: {
    ccb_funding_payment_method: 'Méthode de paiement',
    ccb_funding_payment_method_helper_text:
      "Les retraits CCB sont financés par l'entreprise. Si le prélèvement SEPA est sélectionné, l'entreprise sera prélevée lors de l'approbation du retrait.",
    ccb_funding_payment_method_sepa: 'Prélèvement SEPA',
    ccb_funding_payment_method_transfer: 'Virement bancaire',
    user_message: "Explication pour l'utilisateur en langue utilisateur",
  },
  choices: {
    status: {
      new: 'Nouveau',
      pending: 'En cours',
      completed: 'Complété',
      pending_approval: "En attente d'approbation",
      cancelled: 'Annulé',
      settled: 'Réglé',
    } satisfies Record<B2bWithdrawalStatus, string>,
    orders_flow: {
      regular: 'Classique',
      trigger_nav: 'Seuil de déclenchement',
      floor_nav: 'Valeur plancher',
      floor_price: 'Cours plancher',
    } satisfies Record<B2bWithdrawalOrdersFlow, string>,
    type: {
      regular: 'Classique',
      early: 'Anticipé',
    } satisfies Record<B2bWithdrawalType, string>,
    payment_status: {
      reprocessing: 'Retraitement',
      cancelled: 'Annulé',
      void: 'Annulé',
      returned: 'Retourné',
      held: 'En attente',
      unallocated: 'Non alloué',
      submitted: 'Soumis',
      screening_req: 'Vérification requise',
      validated: 'Validé',
      pending_for_date: 'En attente de date',
      pending_for_funds: 'En attente de fonds',
      ext_proc: 'Traité en externe',
      processed: 'Traité',
      reconciled: 'Réconcilié',
      er_invalid: 'Erreur : invalide',
      er_extconn: 'Erreur : connexion externe',
      er_extsys: 'Erreur : système externe',
      er_expired: 'Erreur : expiré',
      er_general: 'Erreur : générale',
      er_batch: 'Erreur : lot',
      ext_sent: 'Envoyé en externe',
      clearing: 'Compensation',
      held_in_suspense: 'Suspendu',
      shares_canceled: 'Actions annulées',
    } satisfies Record<MdlrPaymentStatus, string>,
    early_reason: {
      marriage: 'Mariage',
      conclusion_of_a_pacs: "Conclusion d'un PACS",
      divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s:
        'Divorce, séparation ou dissolution d’un PACS avec enfant(s) à charge',
      birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child:
        'Naissance ou adoption du 3ème enfant puis de chaque enfant suivant',
      invalidity: 'Invalidité',
      savers_death: 'Décès de l’épargnant',
      death_of_the_pacs_spouse_or_partner: 'Déces du conjoint ou du partenaire de PACS',
      over_indebtedness: 'Surendettement',
      domestic_violence: 'Violences conjugales ',
      creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs:
        'Création ou reprise d’une entreprise par l’épargnant, ses enfants, son conjoint ou partenaire de PACS',
      cessation_of_self_employed_activity: "Cessation d'activité non salarié",
      termination_of_the_employment_contract_or_activity_of_individual_entrepreneur:
        'Cessation du contrat de travail ou d’activité d’entrepreneur individuel',
      expiration_of_unemployment_insurance_rights: "Expiration des droits à l'assurance chômage",
      leaving_for_retirement: 'Départ à la retraite',
      acquisition_of_the_main_residence: 'Acquisition de la résidence principale',
      construction_of_the_main_residence: 'Construction de la résidence principale',
      expansion_of_the_main_residence: 'Agrandissement de la résidence principale',
      rehabilitation_following_a_natural_disaster:
        'Remise en état suite à une catastrophe naturelle',
      no_employment_contract_or_corporate_mandate: 'Absence contrat de travail ou mandat social',
      termination_of_the_social_mandate: 'Cessation du mandat social',
      energy_renovation: 'Rénovation énergétique',
      acquisition_of_shares_in_a_production_cooperative:
        'Acquisition de parts de Société Coopérative de Production (SCOP)',
      judicial_liquidation_or_full_transfer_of_the_company:
        'Liquidation judiciaire ou cession totale de l’entreprise',
      purchase_of_a_low_emission_vehicle: 'Achat d’un véhicule propre',
      caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner:
        'Activité de proche aidant exercée par le titulaire, ou son époux(se) ou partenaire de Pacs',
      option_exercise: "Levée d'option",
      instrument_maturity: "À l'échéance",
      indirect_management: 'Gestion indirecte',
    } satisfies Record<B2BEarlyWithdrawalReason, string>,
  },
};
